import { useMemo } from 'react';
import { pick, prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { useFormatter } from 'js/hooks/useFormatter';
import { useComponentProps } from 'js/hooks/component/useComponentProps';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as Types from '../types';
import * as helpers from '../helpers';
import { useDynamicReducerSelector } from '../store';

export const useBonuses = () => {
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const bonuses = useDynamicReducerSelector(prop('bonuses'));
    const { showcancelbutton } = <Types.IBonusHistoryConfig>useComponentProps();
    const { bonusesConfiguration, bonusManagementTCPreferences } = useAppSelector(pick(['bonusesConfiguration', 'bonusManagementTCPreferences']));
    const { bonusTcMapping, defaultTermsAndCondWcId } = bonusManagementTCPreferences;

    return useMemo(() => bonuses.map((bonus: Types.IBonusParsed): Types.IBonusToDisplay => {
        const { amount, betLimit, acceptDate, amountOnBalance, completionType, endDate,
            freeSpinsCount, freeSpinsCountRemaining, goldenChips, initialWagering, pendingWinnings,
            playType, redeemedAmount, removalReason, status, templateCode, wagering
        } = bonus;

        const isWageringAmountPresent = Boolean(wagering && Number(wagering.amount));
        const isInitialWageringAmountPresent = Boolean(initialWagering && Number(initialWagering.amount));
        const progressBarValue = isWageringAmountPresent && isInitialWageringAmountPresent
            ? String(100 - Math.round(Number(wagering?.amount) / Number(initialWagering?.amount || 1) * 100)) : '';
        const bonusConfig = bonusesConfiguration.find((element) => element.bonusCode === templateCode);
        const bonusTC = bonusTcMapping?.find((element) => element.bonusCode === templateCode);

        return {
            ...bonus,
            progressBarValue,
            popupTitle: translate('playtech.mobile.system.bonusDetail.tcPopupTitle'),
            detailedWebContentId: bonusConfig?.detailedViewWebContentId,
            popupWcId: bonusTC?.termsAndCondWcId || defaultTermsAndCondWcId || '',
            goldenChipsCount: goldenChips ? String(goldenChips.goldenChipsCount) : '',
            freeSpinsCount: freeSpinsCount !== undefined ? String(freeSpinsCount) : '',
            goldenChipsCountRemaining: goldenChips ? String(goldenChips.goldenChipsCountRemaining) : '',
            freeSpinsCountRemaining: freeSpinsCountRemaining !== undefined ? String(freeSpinsCountRemaining) : '',
            endDate: endDate && formatter.getDateTimeShort(formatter.parseImsDate(endDate)) || '',
            acceptDate: acceptDate && formatter.getDateTimeShort(formatter.parseImsDate(acceptDate)) || '',
            amount: formatter.formatCurrency(amount.amount, { currencyCode: amount.currencyCode }) || '',
            removalReason: removalReason && translate(`removalReason.${removalReason}`) || '',
            completionType: completionType && translate(`completionType.${completionType}`) || '',
            isCancelButton: Boolean(showcancelbutton) && (status === 'active' || status === 'accepted' || status === 'waiting'),
            betLimit: betLimit && formatter.formatCurrency(betLimit.amount, { currencyCode: betLimit.currencyCode }) || '',
            amountOnBalance: formatter.formatCurrency(amountOnBalance.amount, { currencyCode: amountOnBalance.currencyCode }),
            redeemedAmount: redeemedAmount && formatter.formatCurrency(redeemedAmount.amount, { currencyCode: redeemedAmount.currencyCode }) || '',
            pendingWinnings: pendingWinnings && Number(pendingWinnings.amount) > 0 ? formatter.formatCurrency(pendingWinnings.amount, { currencyCode: pendingWinnings.currencyCode }) : '',
            initialWagering: initialWagering && Number(initialWagering.amount) ? String(formatter.formatCurrency(initialWagering.amount, { currencyCode: initialWagering.currencyCode })) : '',
            bonusAmount: helpers.getBonusAmount({ bonus, translate, formatCurrency: formatter.formatCurrency }),
            wagering: wagering && Number(wagering.amount) ? formatter.formatCurrency(wagering.amount, { currencyCode: wagering.currencyCode }) : '',
            goldenChipValue: playType === 'goldenchips' && goldenChips?.goldenChipValue ? formatter.formatCurrency(goldenChips.goldenChipValue.amount, { currencyCode: goldenChips.goldenChipValue.currencyCode }) : ''
        };

    }), [bonusTcMapping, bonuses, bonusesConfiguration, defaultTermsAndCondWcId, formatter, showcancelbutton, translate]);
};
